import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRightPanelStore, useWorkspaceStore } from '@/lib/stores';
import {
  X, FileStack, PenSquare, LayoutPanelLeft, Download, Copy,
  GripVertical, Plus, Trash2, Sparkles, FileText, BarChart3,
  TableProperties, ChevronUp, ChevronDown, Loader2, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

// ==================== ARTIFACTS TAB ====================
function ArtifactsTab() {
  const { artifacts } = useWorkspaceStore();
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? artifacts : artifacts.filter(a => a.type === filter);
  const types = [...new Set(artifacts.map(a => a.type))];

  const handleCopyArtifact = (artifact) => {
    const text = artifact.title || artifact.name || artifact.type;
    navigator.clipboard.writeText(text).then(() => toast.success('Copied artifact name'));
  };

  if (!artifacts.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center px-6">
        <FileStack className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">No artifacts yet</p>
        <p className="text-xs text-muted-foreground/60 mt-1">Run an analysis to generate plots, tables, and reports</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      {/* Filter pills */}
      <div className="flex gap-1.5 flex-wrap">
        <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')} className="rounded-full h-7 text-[10px] px-2.5">
          All ({artifacts.length})
        </Button>
        {types.map(t => (
          <Button key={t} size="sm" variant={filter === t ? 'default' : 'outline'}
            onClick={() => setFilter(t)} className="rounded-full h-7 text-[10px] px-2.5 capitalize">
            {t} ({artifacts.filter(a => a.type === t).length})
          </Button>
        ))}
      </div>

      {/* Cards */}
      {filtered.map((artifact, i) => (
        <motion.div key={artifact.id || i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
          <Card className="card-hover">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{artifact.name || artifact.title || artifact.type}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-primary/10 text-primary capitalize">{artifact.type}</span>
                    {artifact.agent && <span className="text-[9px] text-muted-foreground">{artifact.agent}</span>}
                  </div>
                </div>
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleCopyArtifact(artifact)} title="Copy">
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title="Add to Report"
                    onClick={() => {
                      const { addReportBlock } = useWorkspaceStore.getState();
                      addReportBlock({
                        id: Date.now().toString(), type: artifact.type,
                        title: artifact.name || artifact.title || artifact.type,
                        content: `[${artifact.type}] ${artifact.name || artifact.title}`,
                        agent: artifact.agent, order: 0,
                      });
                      toast.success('Added to report');
                    }}>
                    <PenSquare className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

// ==================== REPORT BUILDER TAB ====================
function ReportBuilderTab() {
  const { reportBlocks, addReportBlock, removeReportBlock, reorderReportBlocks } = useWorkspaceStore();
  const { currentSession } = useWorkspaceStore();
  const [stitching, setStitching] = useState(false);
  const [exporting, setExporting] = useState(false);

  const addTextBlock = () => {
    addReportBlock({
      id: Date.now().toString(), type: 'text', title: 'New Section', content: '', order: reportBlocks.length,
    });
  };

  const addSectionHeader = () => {
    addReportBlock({
      id: Date.now().toString(), type: 'header', title: 'Section Title', content: '', order: reportBlocks.length,
    });
  };

  const updateBlock = (id, field, value) => {
    const updated = reportBlocks.map(b => b.id === id ? { ...b, [field]: value } : b);
    reorderReportBlocks(updated);
  };

  const moveBlock = (id, direction) => {
    const idx = reportBlocks.findIndex(b => b.id === id);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= reportBlocks.length) return;
    const copy = [...reportBlocks];
    [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
    reorderReportBlocks(copy);
  };

  const handleAIStitch = async () => {
    if (!reportBlocks.length) { toast.error('Add blocks first'); return; }
    if (!currentSession) { toast.error('No active session'); return; }
    setStitching(true);
    try {
      const res = await axios.post(`${API_BASE}/chat`, {
        message: `Generate an executive summary connecting these report sections: ${reportBlocks.map(b => b.title).join(', ')}. Write in regulatory document style.`,
        session_id: currentSession.id,
      });
      if (res.data.message?.content) {
        addReportBlock({
          id: Date.now().toString(), type: 'text',
          title: 'AI-Generated Executive Summary', content: res.data.message.content, order: 0,
        });
        toast.success('AI Stitch complete — executive summary added');
      }
    } catch (e) {
      toast.error('AI Stitch failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setStitching(false);
    }
  };

  const handleExportDOCX = async () => {
    if (!currentSession) { toast.error('No active session'); return; }
    setExporting(true);
    try {
      const res = await axios.post(`${API_BASE}/reports/export/docx`,
        { session_id: currentSession.id, title: 'Pharmacometric Analysis Report' },
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'Pharmax_Report.docx'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported as DOCX');
    } catch (e) {
      toast.error('Export failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const textCount = reportBlocks.filter(b => b.type === 'text').length;
  const headerCount = reportBlocks.filter(b => b.type === 'header').length;
  const otherCount = reportBlocks.length - textCount - headerCount;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 p-3 border-b border-border/30 flex-wrap">
        <Button size="sm" variant="outline" onClick={addTextBlock} className="rounded-full h-7 text-[10px] px-2.5">
          <Plus className="w-3 h-3 mr-1" /> Text
        </Button>
        <Button size="sm" variant="outline" onClick={addSectionHeader} className="rounded-full h-7 text-[10px] px-2.5">
          <Plus className="w-3 h-3 mr-1" /> Header
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={handleAIStitch} disabled={stitching || !reportBlocks.length}
          className="rounded-full h-7 text-[10px] px-2.5 text-primary">
          {stitching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
          AI Stitch
        </Button>
        <Button size="sm" onClick={handleExportDOCX} disabled={exporting}
          className="rounded-full h-7 text-[10px] px-2.5">
          {exporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
          DOCX
        </Button>
      </div>

      {/* Block list */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {reportBlocks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <PenSquare className="w-8 h-8 text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground">No report blocks yet</p>
              <p className="text-[10px] text-muted-foreground/50 mt-1">Use section hover buttons in chat to add content</p>
            </div>
          ) : (
            reportBlocks.map((block, i) => (
              <motion.div key={block.id} layout initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex gap-1.5 group">
                  {/* Reorder controls */}
                  <div className="flex flex-col items-center gap-0.5 pt-2 opacity-50 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => moveBlock(block.id, 'up')} className="text-muted-foreground hover:text-foreground">
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground/30" />
                    <button onClick={() => moveBlock(block.id, 'down')} className="text-muted-foreground hover:text-foreground">
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Block content */}
                  <div className="flex-1 rounded-lg border border-border/40 p-2.5 bg-muted/5">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        {block.type === 'header' && <FileText className="w-3 h-3 text-primary" />}
                        {block.type === 'text' && <FileText className="w-3 h-3 text-muted-foreground" />}
                        {block.type === 'table' && <TableProperties className="w-3 h-3 text-blue-400" />}
                        {block.type === 'plot' && <BarChart3 className="w-3 h-3 text-green-400" />}
                        <span className="text-[10px] font-mono text-muted-foreground capitalize">{block.type}</span>
                        {block.agent && <span className="text-[9px] text-muted-foreground/50">({block.agent})</span>}
                      </div>
                      <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100"
                        onClick={() => removeReportBlock(block.id)}>
                        <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </div>

                    {block.type === 'header' ? (
                      <Input value={block.title} onChange={(e) => updateBlock(block.id, 'title', e.target.value)}
                        className="h-8 text-sm font-semibold border-0 bg-transparent p-0 focus-visible:ring-0"
                        placeholder="Section title" style={{ fontFamily: 'Space Grotesk' }} />
                    ) : (
                      <>
                        <Input value={block.title} onChange={(e) => updateBlock(block.id, 'title', e.target.value)}
                          className="h-7 text-xs font-medium border-0 bg-transparent p-0 focus-visible:ring-0 mb-1"
                          placeholder="Block title" />
                        <Textarea value={block.content} onChange={(e) => updateBlock(block.id, 'content', e.target.value)}
                          className="text-xs border-0 bg-transparent p-0 focus-visible:ring-0 min-h-[40px] resize-none"
                          placeholder="Write content or paste from chat..." rows={3} />
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="p-2 border-t border-border/30 text-[10px] text-muted-foreground text-center">
        {reportBlocks.length} blocks ({headerCount} headers, {textCount} text{otherCount > 0 ? `, ${otherCount} other` : ''})
      </div>
    </div>
  );
}

// ==================== CANVAS TAB ====================
function CanvasTab() {
  const [content, setContent] = useState('');
  const [format, setFormat] = useState({ bold: false, italic: false });

  const handleCopyAll = () => {
    navigator.clipboard.writeText(content).then(() => toast.success('Canvas content copied'));
  };

  const handleExport = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'canvas_document.txt'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Canvas exported');
  };

  const wordCount = content.split(/\s+/).filter(Boolean).length;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-0.5 p-2 border-b border-border/30">
        <Button size="sm" variant={format.bold ? 'secondary' : 'ghost'} onClick={() => setFormat(f => ({...f, bold: !f.bold}))} className="h-7 w-7 p-0 text-xs font-bold">B</Button>
        <Button size="sm" variant={format.italic ? 'secondary' : 'ghost'} onClick={() => setFormat(f => ({...f, italic: !f.italic}))} className="h-7 w-7 p-0 text-xs italic">I</Button>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={handleCopyAll} disabled={!content} className="h-7 text-[10px] px-2">
          <Copy className="w-3 h-3 mr-1" /> Copy
        </Button>
        <Button size="sm" variant="outline" onClick={handleExport} disabled={!content} className="rounded-full h-7 text-[10px] px-2.5">
          <Download className="w-3 h-3 mr-1" /> Export
        </Button>
      </div>
      <div className="flex-1 p-4">
        <Textarea value={content} onChange={(e) => setContent(e.target.value)}
          placeholder="Start writing your document here...&#10;&#10;Use the chat to ask AI to generate content for specific sections.&#10;Use the Copy button in chat sections to paste content here."
          className={`h-full min-h-[300px] text-sm leading-7 border-0 bg-transparent p-0 focus-visible:ring-0 resize-none ${format.bold ? 'font-bold' : ''} ${format.italic ? 'italic' : ''}`}
          style={{ fontFamily: 'Outfit, sans-serif' }} />
      </div>
      <div className="p-2 border-t border-border/30 flex items-center justify-between text-[10px] text-muted-foreground px-4">
        <span>{wordCount} words · {content.length} chars</span>
      </div>
    </div>
  );
}

// ==================== MAIN RIGHT PANEL ====================
export function RightPanel() {
  const { isOpen, activeTab, close, setTab } = useRightPanelStore();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} className="fixed inset-0 bg-black/20 z-40 lg:hidden" />

          <motion.div
            data-testid="right-panel"
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-screen w-full sm:w-[440px] lg:w-[480px] z-50 glass-strong flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between h-14 px-4 border-b border-border">
              <Tabs value={activeTab} onValueChange={setTab} className="w-full">
                <TabsList data-testid="right-panel-tabs" className="bg-transparent h-auto p-0 gap-0">
                  <TabsTrigger value="artifacts"
                    className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg px-3 py-1.5 text-xs">
                    <FileStack className="w-3.5 h-3.5 mr-1.5" /> Artifacts
                  </TabsTrigger>
                  <TabsTrigger value="report"
                    className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg px-3 py-1.5 text-xs">
                    <PenSquare className="w-3.5 h-3.5 mr-1.5" /> Report
                  </TabsTrigger>
                  <TabsTrigger value="canvas"
                    className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg px-3 py-1.5 text-xs">
                    <LayoutPanelLeft className="w-3.5 h-3.5 mr-1.5" /> Canvas
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Button variant="ghost" size="icon" onClick={close}
                data-testid="right-panel-close-button" className="h-8 w-8 rounded-lg hover:bg-muted/50 flex-shrink-0">
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === 'artifacts' && <ScrollArea className="h-full"><ArtifactsTab /></ScrollArea>}
              {activeTab === 'report' && <ReportBuilderTab />}
              {activeTab === 'canvas' && <CanvasTab />}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
